import React, { useEffect, useState } from 'react';

/** Operator-facing view of the rendezvous pool. Hits the BFF
 *  `/api/rendezvous` every 5 s, renders one card per instance plus a
 *  rolled-up totals row. An unreachable instance shows red instead of
 *  disappearing so the operator sees the gap, not just the survivors. */
export default function RendezvousPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch('/api/rendezvous');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) { setData(j); setError(null); }
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    };
    fetchOnce();
    const interval = setInterval(fetchOnce, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (error && !data) {
    return <div style={{ padding: 20, color: '#c00' }}>Failed to load rendezvous state: {error}</div>;
  }
  if (!data) return <div style={{ padding: 20, color: '#888' }}>Loading…</div>;

  const instances = data.instances || [];
  const totals = instances.filter(i => i.reachable).reduce((acc, i) => ({
    connections: acc.connections + (i.connections || 0),
    register_total: acc.register_total + (i.register_total || 0),
    release_total: acc.release_total + (i.release_total || 0),
    queue_size: acc.queue_size + (i.queue_size || 0),
    queue_recipients: acc.queue_recipients + (i.queue_recipients || 0),
  }), { connections: 0, register_total: 0, release_total: 0, queue_size: 0, queue_recipients: 0 });

  return (
    <div style={{ padding: 20, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: 20, padding: 16, background: '#f0f4f8', borderRadius: 6 }}>
        <div style={{ display: 'flex', gap: 24, fontSize: '0.9rem' }}>
          <Stat label="Total connections" value={totals.connections} />
          <Stat label="Registers (lifetime)" value={totals.register_total} />
          <Stat label="Releases (lifetime)" value={totals.release_total} />
          <Stat label="Queue size" value={totals.queue_size} />
          <Stat label="Queue recipients" value={totals.queue_recipients} />
          <Stat label="Instances" value={`${instances.filter(i => i.reachable).length} / ${instances.length}`} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
        {instances.map((inst, i) => <InstanceCard key={i} inst={inst} />)}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ color: '#666', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function InstanceCard({ inst }) {
  const borderColor = inst.reachable ? '#cfd8dc' : '#c00';
  return (
    <div style={{
      border: `1px solid ${borderColor}`, borderRadius: 6, padding: 14,
      background: inst.reachable ? '#fff' : '#fff5f5',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}>{inst.url}</div>
        <div style={{
          fontSize: '0.75rem', padding: '2px 8px', borderRadius: 10,
          background: inst.reachable ? '#e6f4ea' : '#fdecea',
          color: inst.reachable ? '#1e8e3e' : '#c00',
        }}>{inst.reachable ? 'reachable' : 'unreachable'}</div>
      </div>

      {!inst.reachable && (
        <div style={{ color: '#c00', fontSize: '0.85rem' }}>{inst.error || 'unknown error'}</div>
      )}

      {inst.reachable && (
        <>
          <div style={{ display: 'flex', gap: 16, fontSize: '0.85rem', marginBottom: 12 }}>
            <Mini label="conns" value={inst.connections} />
            <Mini label="register" value={inst.register_total} />
            <Mini label="release" value={inst.release_total} />
            <Mini label="backplane" value={`${inst.backplane_peers} peer${inst.backplane_peers === 1 ? '' : 's'}`} />
          </div>

          {(inst.queue_size > 0 || inst.queue_recipients > 0) && (
            <div style={{ background: '#fff8e1', padding: '6px 10px', borderRadius: 4, fontSize: '0.8rem', marginBottom: 10 }}>
              Queue: {inst.queue_size} frame(s) for {inst.queue_recipients} recipient(s)
            </div>
          )}

          <KindTable title="Frames by kind" rows={[
            ['received', inst.frames_received],
            ['delivered', inst.frames_delivered],
            ['queued', inst.frames_queued],
          ]} />

          {Object.keys(inst.frames_dropped || {}).length > 0 && (
            <KindTable title="Dropped (by reason)" rows={[['dropped', inst.frames_dropped]]} />
          )}

          {(Object.keys(inst.backplane_received || {}).length > 0 || Object.keys(inst.backplane_sent || {}).length > 0) && (
            <KindTable title="Backplane (by op)" rows={[
              ['received', inst.backplane_received],
              ['sent', inst.backplane_sent],
            ]} />
          )}
        </>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div style={{ color: '#888', fontSize: '0.7rem', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '0.95rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

/** A small block of "rowName: kind_a=N1, kind_b=N2, …" lines. Sorted by
 *  value desc so the busy kinds float to the top — useful when scanning
 *  for "what's flowing right now." */
function KindTable({ title, rows }) {
  const allKinds = new Set();
  rows.forEach(([, dict]) => Object.keys(dict || {}).forEach(k => allKinds.add(k)));
  if (allKinds.size === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{title}</div>
      <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
        <tbody>
          {rows.map(([rowName, dict], idx) => {
            const entries = Object.entries(dict || {}).sort((a, b) => b[1] - a[1]);
            if (entries.length === 0) return null;
            return (
              <tr key={idx}>
                <td style={{ color: '#888', paddingRight: 8, verticalAlign: 'top', width: 70 }}>{rowName}</td>
                <td>{entries.map(([k, v]) => (
                  <span key={k} style={{ display: 'inline-block', marginRight: 12 }}>
                    <span style={{ color: '#555' }}>{k}</span>=<span>{v}</span>
                  </span>
                ))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
